import type { Phase } from "@logic/lib/design"
import { PHASES } from "@logic/lib/design"
import { extractMeshFromStore, findFlaws } from "@logic/lib/mesh"
import { MODULE_POWUR_OS_EQUIPMENT_ID } from "@logic/lib/powur-os"
import { selector, atom } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { allEquipmentForSiteState } from "~/containers/Proposal/state/equipment/equipment-state"
import en from "~/i18n/en"
import type { Interlingual } from "~/i18n/types"

import { areaIndex, findAreaState } from "../elements/areas"
import { edgeIndex, findEdgeState } from "../elements/edges"
import { findNodeState, nodeIndex } from "../elements/nodes"
import { panelIndex } from "../elements/panels"
import { getPanelIsError } from "../elements/panels/validation"
import type { TransactionOperation } from "../elements/transactions/transactors"
import { readonlyTransactors } from "../elements/transactions/transactors"

type Blocker = {
  message: Interlingual
  offenders: ReadonlyArray<string>
}

export const getBlockersFor: Record<
  Phase,
  TransactionOperation<undefined, ReadonlyArray<Blocker>>
> = {
  "Image Selection": () => [],
  "Outline": (transactors) => {
    const { get } = transactors,
      nodeIds = get(nodeIndex),
      edgeIds = get(edgeIndex),
      mesh = extractMeshFromStore({
        nodeIds,
        edgeIds,
        getNode: (id) => get(findNodeState(id)),
        getEdge: (id) => get(findEdgeState(id)),
      }),
      flaws = findFlaws(mesh),
      blockers: Blocker[] = []
    // if (flaws.NODE.length) {
    //   blockers.push({
    //     // message: `PhaseBlock_DeadEnds`,
    //     // offenders: flaws.NODE,
    //     message: `PhaseBlock_IncompleteRoof`,
    //     offenders: [],
    //   })
    // }
    // if (flaws.EDGE.length) {
    //   blockers.push({
    //     message: `PhaseBlock_EdgesCross`,
    //     // offenders: flaws.EDGE,
    //     offenders: [],
    //   })
    // }
    if (!nodeIds.length) {
      blockers.push({
        message: `PhaseBlock_NoNodes`,
        offenders: [],
      })
    }

    return blockers
  },
  "Edge Designation": ({ get }) => {
    const areaIds = get(areaIndex)
    const flawedAreaIds = areaIds.filter(
      (id) => get(findAreaState(id)).azimuth === null,
    )
    if (flawedAreaIds.length) {
      return [
        {
          message: `PhaseBlock_IncompleteEdgeDesignation`, // `PhaseBlock_NoAzimuth`,
          offenders: [], //  flawedAreaIds,
        },
      ]
    }

    return []
  },
  "Roof Attributes": ({ get }) => {
    const areaIds = get(areaIndex)
    const allEquipment = get(allEquipmentForSiteState)
    const moduleDataIsAvailable = allEquipment.some(
      (item) => item.typeId === MODULE_POWUR_OS_EQUIPMENT_ID,
    )
    const blockers: Blocker[] = []

    if (!moduleDataIsAvailable) {
      blockers.push({
        message: `PhaseBlock_ModuleDataRequired`,
        offenders: [],
      })
    }

    const flawedAreaIds = areaIds.filter(
      (id) => get(findAreaState(id)).azimuth === null,
    )
    if (flawedAreaIds.length) {
      blockers.push({
        message: `PhaseBlock_IncompleteEdgeDesignation`, // `PhaseBlock_NoAzimuth`,
        offenders: [], //  flawedAreaIds,
      })
    }

    return blockers
  },
  "Panel Placement": ({ get }) => {
    const panelIds = get(panelIndex)
    if (panelIds.length === 0) {
      return [{ message: `PhaseBlock_NoPanels`, offenders: [] }]
    }
    const hasInvalidPanels = panelIds.some((panelId) =>
      getPanelIsError(readonlyTransactors(get), panelId),
    )
    if (hasInvalidPanels) {
      return [{ message: `PhaseBlock_InvalidPanels`, offenders: [] }]
    }
    return []
  },

  "Panel Activation": () => [],
}

export const getNextPhaseBlockers: TransactionOperation<
  undefined,
  ReadonlyArray<Blocker>
> = (transactors) => {
  const { get } = transactors
  const { phase } = get(designLocalState)
  const nextPhase = PHASES[PHASES.indexOf(phase) + 1]
  if (nextPhase) {
    return getBlockersFor[phase](transactors)
  }

  return [{ message: `PhaseBlock_ThisIsTheLast`, offenders: [] }]
}

export const nextPhaseBlockersState = selector<ReadonlyArray<Blocker>>({
  key: `nextPhaseBlockers`,
  get: ({ get }) =>
    getNextPhaseBlockers({
      get,
      set: () => {},
      reset: () => {},
    }),
})

export const preformatBlocker = (
  blocker: Blocker,
  translate: (key: Interlingual) => string = (phrase) =>
    `${en.translation[phrase]}`,
): string =>
  translate(blocker.message) +
  blocker.offenders.map((offender) => `\n •` + offender).join()

export const mayEnterNextPhaseState = selector<boolean>({
  key: `mayGoToNextPhase`,
  get: ({ get }) => get(nextPhaseBlockersState).length === 0,
})

export const designPreventionToastIsOpenState = atom<boolean>({
  key: `designPreventionToastIsOpen`,
  default: false,
})
